import { makeStyles } from '@mui/styles';
import IconButton from 'apollo-react/components/IconButton';
import MenuItem from 'apollo-react/components/MenuItem';
import Popover from 'apollo-react/components/Popover';
import TextField from 'apollo-react/components/TextField';
import ArrowDown from 'apollo-react-icons/ArrowDown';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { handleErrors } from '../../../../actions/ErrorActions';
import { getListFilterValues } from '../../../../api/report-designer';
import { checkTrue, isTrue } from '../../../../utils/commonUtils';
import {
  setSelectedControlObject,
  updateControlProperty,
} from '../../../actions/report-designer.actions';
import { ONE_SIDED_CONDITION_VALUES } from '../../../constants/report-designer.constants';
import {
  getControlProperty,
  getHasValidationErrorByIdAndProp,
  getSelectedControlId,
} from '../../../selectors/report-designer.selectors';
import { getDropDownConditionValuesByAccessor } from '../../../utils/report-designer.utils';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    "& [class*='asteriskLeft']": {
      paddingLeft: 4,
    },
  },
  input: {
    margin: '5px 0px 5px 5px',
    minWidth: 200,
    '& div > input': {
      borderRadius: '4px 0px 0px 4px !important',
    },
  },
  disabled: {
    '& div > input': {
      backgroundColor: '#f2f2f2 !important',
      border: '1px solid #d9d9d9 !important',
      borderRadius: '4px !important',
    },
  },
  notRequired: {
    '& div > input': {
      marginLeft: 4,
    },
  },
  iconButton: {
    marginTop: 7,
    height: 40,
    width: 35,
    border: '1px solid #d9d9d9',
    borderRadius: '0px 4px 4px 0px',
    borderLeft: 'none',
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: '#f2f2f2',
    },
  },
  select: {
    margin: '8px 0',
  },
  icon: {
    width: 16,
    height: 16,
  },
  popover: {
    marginTop: 48,
  },
  listItemContainer: {
    maxHeight: 400,
    padding: '8px 0px',
    minWidth: ({ textFieldWidth }) => textFieldWidth || 'unset',
  },
});

const EditableDropDownCell = ({ row, column }) => {
  const anchorElRef = useRef(null);

  const classes = useStyles({ textFieldWidth: anchorElRef.current?.offsetWidth });
  const dispatch = useDispatch();

  const { accessor } = column;
  const { controlId, condition, allowChange } = row;

  const selectedControlId = useSelector(getSelectedControlId);
  const dataField = useSelector((state) =>
    getControlProperty(state, { id: controlId, prop: 'dataField' })
  );
  const dataMember = useSelector((state) =>
    getControlProperty(state, { id: controlId, prop: 'dataMember' })
  );
  const showValues = useSelector((state) =>
    getControlProperty(state, { id: controlId, prop: 'showValues' })
  );
  const hasValidationError = useSelector((state) =>
    getHasValidationErrorByIdAndProp(state, { id: controlId, prop: accessor })
  );

  const storeValue = row[accessor] || '';
  const isDisabled = accessor === 'value' && ONE_SIDED_CONDITION_VALUES.includes(condition);
  const isRequired = accessor === 'value' && !isDisabled && !isTrue(allowChange);

  const [value, setValue] = useState(storeValue);
  const [values, setValues] = useState(getDropDownConditionValuesByAccessor(accessor));
  const [openList, setOpenList] = useState(false);

  useEffect(() => {
    const fetchFilterList = async () => {
      try {
        const fieldName = dataField.split('.')[1];
        const entityName = dataMember.split('.')[1];

        const response = await getListFilterValues(entityName, fieldName);

        if (response.hasError) {
          dispatch(handleErrors(response.errors));
          return;
        }

        const filterValues = response.filterValues?.filterTable || [];
        const filterValueToDisplay = filterValues.map(({ filterValue }) => ({
          value: filterValue,
          label: filterValue,
        }));

        setValues(filterValueToDisplay);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(`getListFilterValues() failed: ${error}`);
      }
    };

    if (dataMember && dataField && showValues && checkTrue(showValues.toString())) {
      fetchFilterList();
    } else {
      setValues(getDropDownConditionValuesByAccessor(accessor));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataField, dataMember, showValues]);

  useEffect(() => {
    setValue(storeValue);
  }, [storeValue]);

  const handleMouseDownClick = () => {
    if (controlId !== selectedControlId) {
      dispatch(setSelectedControlObject(controlId, false));
    }
  };

  const handleBlur = ({ target }) => {
    if (target.value !== storeValue) {
      dispatch(updateControlProperty(controlId, accessor, target.value));
    }
  };

  const handleMenuOptionClick = (value) => {
    if (value !== storeValue) {
      dispatch(updateControlProperty(controlId, accessor, value));
    }
    setOpenList(false);
  };

  return (
    <>
      <div className={classes.root}>
        <TextField
          fullWidth
          required={isRequired}
          asteriskPosition={isRequired ? 'before' : null}
          value={value ?? ''}
          className={classNames({
            [classes.input]: true,
            [classes.disabled]: isDisabled,
            [classes.notRequired]: !isRequired,
          })}
          ref={anchorElRef}
          onBlur={handleBlur}
          onChange={({ target: { value } }) => setValue(value)}
          onMouseDown={handleMouseDownClick}
          error={hasValidationError}
          disabled={isDisabled}
        />
        {!isDisabled && (
          <IconButton
            className={classes.iconButton}
            onClick={() => setOpenList(!openList)}
            onMouseDown={handleMouseDownClick}
          >
            <ArrowDown className={classes.icon} />
          </IconButton>
        )}
        <Popover
          onClose={() => setOpenList(!openList)}
          open={openList}
          anchorEl={anchorElRef.current}
          className={classes.popover}
        >
          <div className={classes.listItemContainer}>
            {values.map(({ value, label }) => (
              <MenuItem key={value} value={value} onClick={() => handleMenuOptionClick(value)}>
                {label}
              </MenuItem>
            ))}
          </div>
        </Popover>
      </div>
    </>
  );
};

export default EditableDropDownCell;
